/**
 * Canonical display labels for workflow status values.
 *
 * Single source of truth used by both the ora app and the Outlook add-in.
 * Import in ora as:   ~/common/workflows/statusLabels
 * Import in add-in as: @product-app/common/workflows/statusLabels
 */
/**
 * What every waiting-on-you status reads, everywhere (2026-10-05). Was
 * "Input Needed" / "Warm Hold" / "Input required" / "Needs your input" by
 * surface. Rendered in the shared pill (`statusPill.ts`).
 */
export const NEEDS_YOU_LABEL = "Needs You";

export const STATUS_LABELS: Record<string, string> = {
  failed: "Error",
  paused_hitl: NEEDS_YOU_LABEL,
  // The held Epic session is an implementation detail; to the Servicer it is
  // the same wait.
  warm_hitl: NEEDS_YOU_LABEL,
  rpa_review: NEEDS_YOU_LABEL,
  running: "Running",
  completed: "Completed",
  cancelled: "Cancelled",
  timeout_cancelled: "Timed Out",
  rejected: "Rejected",
  pending_email_send: "Pending Email Send",
  pending_edocs: "Pending Documents",
};

/**
 * Returns the canonical display label for a substrate status string.
 * Falls back to title-casing the raw value if not in the map.
 */
export function statusDisplayLabel(status: string): string {
  const key = status.toLowerCase();
  return (
    STATUS_LABELS[key] ??
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * Per-workflow wording where the generic status and step read wrong, keyed by
 * substrate workflow_id, then status.
 *
 * A quote comparison paused at its review gate has a finished report waiting
 * for the AM to review and finalize, so "Needs You" undersells it: it reads
 * "Review Required", with "In Review" as its step (operator, 2026-09-23).
 */
export const WORKFLOW_STATUS_OVERRIDES: Record<
  string,
  Record<string, { status: string; step: string }>
> = {
  quote_compare_v1: {
    paused_hitl: { status: "Review Required", step: "In Review" },
  },
  // A workflow-skill analysis (document-analysis S3) holds for review the same way.
  document_analysis_v1: {
    paused_hitl: { status: "Review Required", step: "In Review" },
  },
};

/** The override for this workflow in this status, or null for the generic wording. */
export function workflowStatusOverride(
  workflowType: string | null | undefined,
  status: string,
): { status: string; step: string } | null {
  if (!workflowType) return null;
  return WORKFLOW_STATUS_OVERRIDES[workflowType]?.[status.toLowerCase()] ?? null;
}

/**
 * Human-friendly display names for specific workflow steps, keyed by the
 * substrate step_id. Overrides the catalog/titlecased fallback where the raw
 * step id would read as jargon. Shared by ora and the add-in.
 *
 * `edoc_wait` is the carrier eDoc cold gate — "Awaiting Carrier Documents"
 * reads far better than the titlecased "Edoc Wait".
 */
export const STEP_DISPLAY_OVERRIDES: Record<string, string> = {
  edoc_wait: "Awaiting Carrier Documents",
};

/**
 * Resolve a step's display name: explicit override → provided fallback
 * (catalog display name) → title-cased step id.
 */
export function stepDisplayName(
  stepId: string | null | undefined,
  fallback?: string | null,
): string | null {
  if (!stepId) return fallback ?? null;
  return (
    STEP_DISPLAY_OVERRIDES[stepId] ??
    fallback ??
    stepId.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}
